import { getVariant } from '@apia/theme';
import { arrayOrArray, TApiaFormElement, TApiaLoad } from '@apia/util';
import uniqueId from 'lodash-es/uniqueId';
import React from 'react';
import { Box } from '@apia/theme';
import { getFunction, TApiaApiMethodHandler } from '../ApiaApiHandler';
import { TModalTable } from '../types';
import { ApiaApiCheckbox } from './ApiaApiCheckbox';
import { ApiaApiFileInput } from './ApiaApiFileInput';
import { ApiaApiInput } from './ApiaApiInput';
import { ApiaApiRadio } from './ApiaApiRadio';
import { ApiaApiSelect } from './ApiaApiSelect';
import { ApiaApiTextArea } from './ApiaApiTextArea';
import { ApiaApiEnvironmentsModalField } from './ApiaApiEnvironmentsModalField';
import { ApiaApiTasksModalField } from './ApiaApiTasksModalField';
import { ApiaApiProcessesModalField } from './ApiaApiProcessesModalField';
import { ApiaApiPoolsModalField } from './ApiaApiPoolsModalField';
import { ApiaApiUsersModalField } from './ApiaApiUsersModalField';

type TApiaApiFieldsContainer = TApiaApiMethodHandler & {
  definition?: TApiaLoad | undefined;
};

type TApiaApiSectionElement = TApiaFormElement & {
  sectionElementId: string;
};

type TApiaApiSection = {
  sectionId: string;
  sectionHeader?: TApiaApiSectionElement;
  sectionElements: TApiaApiSectionElement[];
};

const parseApiaApiSections = (elements: TApiaFormElement[]) => {
  const removeEndingSpacers = (
    sectionElements: TApiaFormElement[],
  ): TApiaFormElement[] => {
    if (sectionElements.length > 1) {
      for (let i = sectionElements.length - 1; i >= 0; i--) {
        if (sectionElements[i].type === 'empty') {
          sectionElements.pop();
        } else {
          return sectionElements;
        }
      }
    }
    return sectionElements;
  };
  const sectionIndexes: number[] = [];
  if (elements[0]?.type !== '2columnTitle') {
    sectionIndexes.push(-1);
  }
  elements.forEach((element, index) => {
    if (element.type === '2columnTitle') {
      sectionIndexes.push(index);
    }
  });
  const sections: TApiaApiSection[] = [];
  sectionIndexes.forEach((sectionIndex, arrayIndex) => {
    const start =
      sectionIndex + 1 < elements.length
        ? sectionIndex + 1
        : elements.length - 1;
    const end =
      arrayIndex + 1 < sectionIndexes.length
        ? sectionIndexes[arrayIndex + 1]
        : undefined;
    sections.push({
      sectionId: `ApiaApiSection_${arrayIndex}`,
      sectionHeader:
        sectionIndex !== -1
          ? {
              ...elements[sectionIndex],
              sectionElementId: `sectionHeader_${arrayIndex}`,
            }
          : undefined,
      sectionElements: removeEndingSpacers(elements.slice(start, end)).map(
        (currentElement) => ({
          ...currentElement,
          sectionElementId: `sectionElement_${
            currentElement.id || currentElement.name || uniqueId()
          }`,
        }),
      ),
    });
  });
  return sections;
};

const NonMemoizedApiaApiFieldsContainer = (props: TApiaApiFieldsContainer) => {
  const elements = React.useMemo(
    () => arrayOrArray(props?.definition?.form.elements?.element ?? []),
    [props?.definition?.form.elements?.element],
  );

  const sections = React.useMemo(
    () => parseApiaApiSections(elements),
    [elements],
  );

  const renderSectionContent = React.useCallback(
    (sectionId: string, sectionElements: TApiaApiSectionElement[]) => {
      const isVisible =
        sectionElements.findIndex((element) => element.type !== 'hidden') !==
        -1;

      return (
        <Box
          className={
            isVisible
              ? 'handler__form__elements__section__content'
              : `handler__form__elements__section__content handler__hidden`
          }
          key={sectionId}
        >
          {sectionElements.map((current) => {
            const element = {
              ...current,
              onChange() {
                getFunction(current.onChange, props)
                  .then((onChangeMethod) => {
                    if (onChangeMethod) {
                      onChangeMethod();
                    }
                  })
                  .catch(console.error);
              },
            };
            switch (element.type) {
              case 'table': {
                const data = JSON.parse(element.text) as TModalTable;
                return (
                  <Box
                    key={element.sectionElementId}
                    {...getVariant('layout.common.tables.information')}
                  >
                    <table sx={{ width: '100%' }}>
                      <thead>
                        <tr>
                          {data.columns.map((column) => {
                            return <th key={column}>{column}</th>;
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {data.rows.map((row) => {
                          return (
                            <tr key={row.cells.join('-')}>
                              {row.cells.map((cell, i) => {
                                return (
                                  <td key={`${cell}_${data.columns[i]}`}>
                                    {cell}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Box>
                );
              }
              case '2columnSubTitle':
                return (
                  <Box
                    key={element.sectionElementId}
                    className={element.class}
                    as="h6"
                  >
                    {element.value || element.text}
                  </Box>
                );
              case '2column':
                return (
                  <Box
                    key={element.sectionElementId}
                    dangerouslySetInnerHTML={{
                      __html: element.value || element.text,
                    }}
                    className={element.class}
                  />
                );
              case 'checkbox':
                return (
                  <ApiaApiCheckbox
                    key={element.sectionElementId}
                    {...props}
                    element={element}
                  />
                );
              case 'file':
                return (
                  <ApiaApiFileInput
                    key={element.sectionElementId}
                    {...props}
                    element={element}
                  />
                );
              case 'hidden':
              case 'password':
              case 'text':
                return (
                  <ApiaApiInput
                    key={element.sectionElementId}
                    {...props}
                    element={element}
                  />
                );
              case 'select':
                return (
                  <ApiaApiSelect
                    key={element.sectionElementId}
                    {...props}
                    element={element}
                  />
                );
              case 'radio':
                return (
                  <ApiaApiRadio
                    key={element.sectionElementId}
                    {...props}
                    element={element}
                  />
                );
              case 'textarea':
                return (
                  <ApiaApiTextArea
                    key={element.sectionElementId}
                    {...props}
                    element={element}
                  />
                );
              case 'empty':
                return (
                  <Box key={element.sectionElementId} className="spacer" />
                );
              case 'envsModal':
                return (
                  <ApiaApiEnvironmentsModalField
                    key={element.sectionElementId}
                    {...props}
                    element={element}
                  />
                );
              case 'tsksModal':
                return (
                  <ApiaApiTasksModalField
                    key={element.sectionElementId}
                    {...props}
                    element={element}
                  />
                );
              case 'procModal':
                return (
                  <ApiaApiProcessesModalField
                    key={element.sectionElementId}
                    {...props}
                    element={element}
                  />
                );
              case 'poolModal':
                return (
                  <ApiaApiPoolsModalField
                    key={element.sectionElementId}
                    {...props}
                    element={element}
                  />
                );
              case 'userModal':
                return (
                  <ApiaApiUsersModalField
                    key={element.sectionElementId}
                    {...props}
                    element={element}
                  />
                );
              default:
                console.warn(
                  `Unhandled element type: ${element.type}`,
                  element,
                );
                return null;
            }
          })}
        </Box>
      );
    },
    [props],
  );

  return (
    <Box className="handler__form__elements">
      {sections.map((section) => {
        if (section.sectionHeader) {
          return renderSectionContent(
            section.sectionId,
            section.sectionElements,
          );
        }
        return renderSectionContent(section.sectionId, section.sectionElements);
      })}
    </Box>
  );
};

export const ApiaApiFieldsContainer = React.memo(
  NonMemoizedApiaApiFieldsContainer,
);

ApiaApiFieldsContainer.displayName = 'ApiaApiFieldsContainer';
